import Cookies from "js-cookie";
import {AlertCircleIcon, AlertTriangleIcon, CheckCircleIcon, ChevronDownIcon, ChevronRightIcon, CompassIcon, InfoIcon, Loader2Icon, RefreshCwIcon, TrendingDownIcon, XCircleIcon} from "lucide-react";
import {useEffect, useRef, useState} from "react";
import {toast} from "sonner";

import {Badge} from "@components/ui/badge";
import {Button} from "@components/ui/button";
import {Card, CardContent} from "@components/ui/card";
import {Input} from "@components/ui/input";
import {Skeleton} from "@components/ui/skeleton";
import {
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TableHeader,
    TableRow,
} from "@components/ui/table";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@components/ui/tooltip";
import {
    CATEGORY_LABELS,
    CONFIDENCE_STYLES,
    type ActualWeek,
    type APRollforwardWeek,
    type ARRollforwardWeek,
    type CashForecastData,
    type ConcentrationRisk,
    type ForecastCategoryAmounts,
    type ForecastWeek,
    type TieOutData,
    type WeeklyOverride,
    clearCashForecastOverride,
    saveCashForecastOverride,
    useCashForecastScoped,
} from "@hooks/use-cashflow";
import {formatCurrency, formatWeeks} from "@lib/format";

import {CashFlowDetailedTour} from "./CashFlowDetailedTour";

// ── Row grouping ───────────────────────────────────────────

interface RowGroup {
    label: string;
    keys: string[];
    type: "inflow" | "outflow";
}

const ROW_GROUPS: RowGroup[] = [
    {
        label: "Inflows",
        keys: ["customerReceipts", "stripeOnlineReceipts", "otherOperatingReceipts", "loanDrawdowns", "equityInjections", "unclassifiedCashReceipts"],
        type: "inflow",
    },
    {
        label: "Cost of Sales / Payroll",
        keys: ["supplierPayments", "payrollNetWages", "superannuation", "payrollTax"],
        type: "outflow",
    },
    {
        label: "Operating Expenses",
        keys: ["gstBasPayments", "contractors", "softwareSaaSSubscriptions", "hostingCloudInfrastructure", "rentOccupancy", "marketingAgenciesPaidMedia", "professionalFees", "bankFeesFinanceCharges", "otherOperatingOutflow", "unclassifiedCashPayments"],
        type: "outflow",
    },
    {
        label: "Investing",
        keys: ["capexEquipmentFixedAssets", "capitalisedSoftware"],
        type: "outflow",
    },
    {
        label: "Financing / Tax",
        keys: ["loanRepayments", "interestPayments", "dividendsOwnerDraws"],
        type: "outflow",
    },
];

// ── Helpers ────────────────────────────────────────────────

function formatWeekHeader(isoDate: string): string {
    const d = new Date(isoDate + "T00:00:00");
    return d.toLocaleDateString("en-US", {month: "short", day: "numeric"});
}

function formatColumnDate(col: {status: "actual" | "forecast"; weekStarting: string; weekEnding?: string}): string {
    if (col.status === "actual") {
        return `End ${formatWeekHeader(col.weekEnding ?? col.weekStarting)}`;
    }
    return formatWeekHeader(col.weekStarting);
}


// ── KPI Card ───────────────────────────────────────────────

function KpiCard({label, value, subtext, alert}: {label: string; value: string; subtext?: string; alert?: boolean}) {
    return (
        <Card className={`flex-1 min-w-[140px] ${alert ? "border-red-300 bg-red-50" : ""}`}>
            <CardContent className="p-3">
                <p className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</p>
                <p className={`text-lg font-bold tabular-nums mt-0.5 ${alert ? "text-red-600" : ""}`}>{value}</p>
                {subtext && <p className="text-[10px] text-muted-foreground mt-0.5">{subtext}</p>}
            </CardContent>
        </Card>
    );
}

// ── Alert banner ───────────────────────────────────────────

const ALERT_ICON = {
    critical: AlertCircleIcon,
    warning: AlertTriangleIcon,
    info: InfoIcon,
};
const ALERT_STYLE = {
    critical: "bg-red-50 border-red-200 text-red-800",
    warning: "bg-amber-50 border-amber-200 text-amber-800",
    info: "bg-blue-50 border-blue-200 text-blue-700",
};

// ── Assumptions panel ─────────────────────────────────────

function AssumptionsPanel() {
    const [open, setOpen] = useState(false);
    return (
        <div className="rounded-md border border-[#209DB3]/30 bg-[#F0FDFA] text-xs text-slate-700" data-tour="cashflow-assumptions">
            <button
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center gap-2 px-3 py-2.5 font-semibold text-[#209DB3] hover:bg-[#209DB3]/10 transition-colors rounded-md"
            >
                {open ? <ChevronDownIcon className="size-4" /> : <ChevronRightIcon className="size-4" />}
                Model Assumptions & Methodology
            </button>
            {open && (
                <div className="px-3 pb-3 pt-1 space-y-3 border-t border-[#209DB3]/20">
                    <div>
                        <p className="font-semibold text-slate-800 mb-1">Actuals (completed weeks)</p>
                        <ul className="list-disc pl-4 space-y-0.5">
                            <li>Opening / closing cash anchored to <span className="font-medium">Xero Bank Summary</span> weekly snapshots</li>
                            <li>Customer Receipts classified from <span className="font-medium">Xero Payments API</span> (invoice-linked ACCREC payments)</li>
                            <li>Remaining cash movements appear as <span className="font-medium">Unclassified Cash Receipts / Payments</span> (bank summary plug)</li>
                            <li>Payroll, super, supplier payments, and other categories classified from bank transactions</li>
                        </ul>
                    </div>
                    <div>
                        <p className="font-semibold text-slate-800 mb-1">Forecast baseline</p>
                        <ul className="list-disc pl-4 space-y-0.5">
                            <li>Each category uses a <span className="font-medium">weekly average</span> of the prior 13 weeks of actuals</li>
                            <li>Confidence tiers: weeks 1-2 high, weeks 3-4 medium, weeks 5+ low</li>
                        </ul>
                    </div>
                    <div>
                        <p className="font-semibold text-slate-800 mb-1">AR / AP roll-forward</p>
                        <ul className="list-disc pl-4 space-y-0.5">
                            <li>Outstanding Xero invoices are scheduled by due date into forecast weeks</li>
                            <li>Customer Receipts = <span className="font-medium">max(weekly average, scheduled AR)</span> — the higher of the two is used to avoid double-counting invoices already reflected in the historical average</li>
                            <li>Supplier Payments = <span className="font-medium">max(weekly average, scheduled AP)</span> — same logic</li>
                            <li>Repeating invoices are <span className="font-medium">additive</span> (confirmed future commitments not yet in actuals)</li>
                            <li className="text-amber-700">If the weekly average exceeds scheduled AR/AP in every week, forecast receipts will appear flat. Hover each forecast cell to see the breakdown. Seasonality adjustments coming soon.</li>
                        </ul>
                    </div>
                    <div>
                        <p className="font-semibold text-slate-800 mb-1">Expense & tax assumptions</p>
                        <ul className="list-disc pl-4 space-y-0.5">
                            <li>Payroll, super, rent, software, contractors — weekly average of actuals</li>
                            <li>GST/BAS — cycle-based: placed in specific weeks when payment is due, zero otherwise</li>
                            <li>Loan repayments — weekly average (validation against Xero terms planned)</li>
                        </ul>
                    </div>
                    <div>
                        <p className="font-semibold text-slate-800 mb-1">Balance chain</p>
                        <ul className="list-disc pl-4 space-y-0.5">
                            <li>Closing Cash = Opening Cash + Net Cash Flow (verified by Formula Tie badge)</li>
                            <li>Each forecast week opens at the prior week&apos;s closing balance</li>
                        </ul>
                    </div>
                    <div>
                        <p className="font-semibold text-slate-800 mb-1">How to update the forecast</p>
                        <ul className="list-disc pl-4 space-y-0.5">
                            <li><span className="font-medium">Refresh data</span> — click the Refresh button to pull latest actuals from Xero (bank transactions, invoices, payments, bank summary)</li>
                            <li><span className="font-medium">Manual overrides</span> — click any forecast cell to enter a manual amount; overrides persist until cleared</li>
                            <li><span className="font-medium">Ask the Virtual CFO</span> — use the chat to ask questions like &quot;what if customer receipts drop 20%?&quot; or &quot;who paid us this week?&quot;</li>
                            <li><span className="font-medium">Actuals roll forward automatically</span> — as new weeks close in Xero, they shift from forecast to actual on next refresh</li>
                        </ul>
                    </div>
                    <p className="text-[10px] text-slate-500 pt-1">
                        This 13-week model is a standalone weekly cash forecast. Monthly scenario changes in the Modeling Workbench do not flow into this view.
                    </p>
                </div>
            )}
        </div>
    );
}

// ── Main component ─────────────────────────────────────────

function formatRelativeTime(iso: string): string {
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
}

function StaleSyncWarning({lastSyncedAt, cashAnchorPeriod}: {lastSyncedAt?: string | null; cashAnchorPeriod?: string | null}) {
    const isStale = !lastSyncedAt || (Date.now() - new Date(lastSyncedAt).getTime() > 24 * 60 * 60 * 1000);
    if (!isStale) return null;
    const anchorLabel = cashAnchorPeriod
        ? new Date(cashAnchorPeriod + (cashAnchorPeriod.length === 7 ? "-01" : "")).toLocaleString("en-AU", {month: "short", year: "numeric"})
        : null;
    return (
        <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <AlertTriangleIcon className="size-4 shrink-0" />
            <span>
                Xero data may be stale{lastSyncedAt ? ` (last synced ${formatRelativeTime(lastSyncedAt)})` : ""}.
                {anchorLabel ? ` Cash anchor: ${anchorLabel}.` : ""}
                {" "}Click Refresh to update.
            </span>
        </div>
    );
}

function ScenarioSeparationDisclaimer() {
    return (
        <div
            data-testid="week13-scenario-disclaimer"
            className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600"
        >
            Monthly scenario changes in the 3-statement forecast do not flow into this panel yet.
            Use this as the tactical 13-week cash view until weekly and monthly scenario logic is linked.
        </div>
    );
}

interface EditingCell {
    cellId: string;
    weekNumber: number;
    categoryKey: string;
    field: "inflow" | "outflow";
    currentValue: number;
    overrideOpId?: string;
}

export function Week13ForecastPanel({
    workspaceId,
    scenarioId,
}: {
    workspaceId?: string;
    scenarioId?: string;
}) {
    const {data, isLoading, error, refresh} = useCashForecastScoped(workspaceId, scenarioId);
    const [mode, setMode] = useState<"forecast" | "variance">("forecast");
    const [refreshing, setRefreshing] = useState(false);
    const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
    const [editValue, setEditValue] = useState("");
    const [editNote, setEditNote] = useState("");
    const [savingCell, setSavingCell] = useState(false);
    const [cashFlowTourOpen, setCashFlowTourOpen] = useState(false);
    const [tourActiveTab, setTourActiveTab] = useState("cashflow_explorer");
    const editInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!editingCell) return;
        const id = window.requestAnimationFrame(() => editInputRef.current?.focus());
        return () => window.cancelAnimationFrame(id);
    }, [editingCell]);

    useEffect(() => {
        const handler = (event: Event) => {
            const nextTab = (event as CustomEvent<{tab?: string}>).detail?.tab;
            if (nextTab) setTourActiveTab(nextTab);
        };
        window.addEventListener("dashboard-tab-changed", handler);
        return () => window.removeEventListener("dashboard-tab-changed", handler);
    }, []);

    const handleTourTabChange = (tab: string) => {
        setTourActiveTab(tab);
        window.dispatchEvent(new CustomEvent("vnext-ui-action", {detail: {tab}}));
    };

    const startEdit = (cell: EditingCell) => {
        setEditingCell(cell);
        setEditValue(String(Math.abs(cell.currentValue)));
    };

    const cancelEdit = () => {
        setEditingCell(null);
        setEditValue("");
        setEditNote("");
    };

    const commitEdit = async () => {
        if (!editingCell || savingCell) return;
        const trimmed = editValue.trim();
        setSavingCell(true);
        try {
            if (trimmed === "") {
                if (editingCell.overrideOpId) {
                    await clearCashForecastOverride({
                        workspaceId,
                        scenarioId,
                        weekNumber: editingCell.weekNumber,
                        categoryKey: editingCell.categoryKey,
                        field: editingCell.field,
                        value: null,
                        opId: editingCell.overrideOpId,
                    });
                }
            } else {
                const numeric = Number(trimmed.replace(/,/g, ""));
                if (!Number.isFinite(numeric) || numeric < 0) {
                    throw new Error("Override value must be a non-negative number.");
                }
                const noteText = editNote.trim();
                const labelParts = [`Override W${editingCell.weekNumber} ${editingCell.categoryKey} ${editingCell.field}`];
                if (noteText) labelParts.push(noteText);
                await saveCashForecastOverride({
                    workspaceId,
                    scenarioId,
                    weekNumber: editingCell.weekNumber,
                    categoryKey: editingCell.categoryKey,
                    field: editingCell.field,
                    value: numeric,
                    label: labelParts.join(" — "),
                });
            }
            cancelEdit();
            await refresh();
        } catch (err) {
            toast.error(err instanceof Error ? err.message : "Failed to save weekly override.");
        } finally {
            setSavingCell(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            const postRes = await fetch("/api/cash/forecast/refresh/", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": Cookies.get("csrftoken") || "",
                },
                credentials: "include",
            });
            const postData = await postRes.json();

            // If sync ran synchronously, revalidate immediately
            if (postData.mode === "sync") {
                refresh();
                setRefreshing(false);
                return;
            }

            // Poll the entity-specific status endpoint until all entities are done
            const pollSync = async (attempts = 0): Promise<void> => {
                if (attempts > 30) {
                    refresh();
                    setRefreshing(false);
                    return;
                }
                await new Promise((r) => setTimeout(r, 2000));
                try {
                    const res = await fetch("/api/cash/forecast/refresh/", {credentials: "include"});
                    const status = await res.json();
                    if (status.isRunning) {
                        return pollSync(attempts + 1);
                    }
                } catch {
                    // Fall through to revalidate
                }
                refresh();
                setRefreshing(false);
            };
            pollSync();
        } catch {
            setRefreshing(false);
        }
    };

    if (isLoading) {
        return (
            <Card className="mt-4 shrink-0">
                <CardContent className="p-6 space-y-4">
                    <div className="flex gap-3">
                        {[1, 2, 3, 4].map((i) => (
                            <Skeleton key={i} className="h-16 flex-1" />
                        ))}
                    </div>
                    <Skeleton className="h-64 w-full" />
                </CardContent>
            </Card>
        );
    }

    const refreshButton = (
        <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="mt-3 h-8 px-3 text-xs"
        >
            {refreshing ? (
                <Loader2Icon className="size-3.5 animate-spin mr-1" />
            ) : (
                <RefreshCwIcon className="size-3.5 mr-1" />
            )}
            {refreshing ? "Syncing..." : "Refresh from Xero"}
        </Button>
    );

    if (error) {
        return (
            <Card className="mt-4 shrink-0">
                <CardContent className="p-6 text-center text-red-600">
                    <AlertCircleIcon className="mx-auto mb-2 size-6" />
                    <p className="text-sm font-medium">Failed to load forecast</p>
                    <p className="text-xs mt-1">{error}</p>
                    {refreshButton}
                </CardContent>
            </Card>
        );
    }

    if (!data || !data.connected) {
        // Distinguish "Xero not connected" from "connected but needs sync"
        const msg = data?.message || "";
        const needsSync = msg.includes("Refresh") || msg.includes("snapshot") || msg.includes("sync");
        return (
            <Card className="mt-4 shrink-0">
                <CardContent className="p-6 text-center text-muted-foreground">
                    <TrendingDownIcon className="mx-auto mb-2 size-6" />
                    <p className="text-sm font-medium">
                        {needsSync ? "Xero data needs syncing" : "Connect Xero to view 13-week forecast"}
                    </p>
                    <p className="text-xs mt-1">
                        {msg || "Link your Xero account to generate weekly cash flow projections."}
                    </p>
                    {needsSync && refreshButton}
                </CardContent>
            </Card>
        );
    }

    const weeks = data.weeks ?? [];
    if (weeks.length === 0) {
        return (
            <Card className="mt-4 shrink-0">
                <CardContent className="p-6 text-center text-muted-foreground">
                    <p className="text-sm font-medium">No forecast data available</p>
                    <p className="text-xs mt-1">Click Refresh to sync from Xero and generate weekly projections.</p>
                    {refreshButton}
                </CardContent>
            </Card>
        );
    }

    const actuals = data.actuals ?? [];

    return (
        <Card className="mt-4 shrink-0" data-tour="cashflow-panel">
            <CardContent className="p-4 space-y-4">
                {/* Mode toggle + Refresh */}
                <div className="flex items-center gap-2" data-tour="cashflow-controls">
                    <button
                        onClick={() => setMode("forecast")}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                            mode === "forecast"
                                ? "bg-[#209DB3] text-white"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200 border border-[#5BF6C8]/40"
                        }`}
                    >
                        Forecast
                    </button>
                    <button
                        onClick={() => setMode("variance")}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                            mode === "variance"
                                ? "bg-[#209DB3] text-white"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200 border border-[#5BF6C8]/40"
                        }`}
                    >
                        Variance
                    </button>
                    <div className="ml-auto flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCashFlowTourOpen(true)}
                            className="h-7 px-2 text-xs border-[#5BF6C8] text-[#209DB3] hover:bg-[#209DB3]/10"
                        >
                            <CompassIcon className="mr-1 size-3.5" />
                            Cash Flow Tour
                        </Button>
                        {data?.lastSyncedAt && (
                            <span className="text-[10px] text-muted-foreground">
                                Updated {formatRelativeTime(data.lastSyncedAt)}
                            </span>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRefresh}
                            disabled={refreshing}
                            className="h-7 px-2 text-xs"
                        >
                            {refreshing ? (
                                <Loader2Icon className="size-3.5 animate-spin mr-1" />
                            ) : (
                                <RefreshCwIcon className="size-3.5 mr-1" />
                            )}
                            Refresh
                        </Button>
                    </div>
                </div>

                <StaleSyncWarning lastSyncedAt={data.lastSyncedAt} cashAnchorPeriod={data.cashAnchorPeriod} />
                <ScenarioSeparationDisclaimer />
                <AssumptionsPanel />

                <KpiStrip data={data} />
                {data.tieOut && <TieOutStrip tieOut={data.tieOut} />}

                {mode === "variance" ? (
                    <>
                        <VarianceTable actuals={actuals} />
                        <WeeklyMatrix
                            weeks={weeks}
                            actuals={actuals.slice(-6)}
                            currentCash={data.currentCash ?? 0}
                            weeklyOverrides={data.weeklyOverrides}
                            editingCell={editingCell}
                            editValue={editValue}
                            editNote={editNote}
                            savingCell={savingCell}
                            editInputRef={editInputRef}
                            onEditValueChange={setEditValue}
                            onEditNoteChange={setEditNote}
                            onStartEdit={startEdit}
                            onCancelEdit={cancelEdit}
                            onCommitEdit={commitEdit}
                        />
                    </>
                ) : (
                    <WeeklyMatrix
                        weeks={weeks}
                        actuals={actuals.slice(-6)}
                        currentCash={data.currentCash ?? 0}
                        weeklyOverrides={data.weeklyOverrides}
                        editingCell={editingCell}
                        editValue={editValue}
                        editNote={editNote}
                        savingCell={savingCell}
                        editInputRef={editInputRef}
                        onEditValueChange={setEditValue}
                        onEditNoteChange={setEditNote}
                        onStartEdit={startEdit}
                        onCancelEdit={cancelEdit}
                        onCommitEdit={commitEdit}
                    />
                )}

                {data.scenarios && <ScenarioStrip scenarios={data.scenarios} />}
                {data.alerts && data.alerts.length > 0 && <AlertBanners alerts={data.alerts} />}

                {/* AR Roll-Forward + Concentration Risk */}
                {data.controlReconciliation && <ControlReconciliationNote data={data} />}
                {data.arRollforward && data.arRollforward.length > 0 && (
                    <ARRollforwardTable rows={data.arRollforward} />
                )}
                {data.apRollforward && data.apRollforward.length > 0 && (
                    <APRollforwardTable rows={data.apRollforward} />
                )}
                {data.concentrationRisk && data.concentrationRisk.topCustomers.length > 0 && (
                    <ConcentrationRiskPanel data={data.concentrationRisk} />
                )}
                {data.collectionsCurve && <CollectionsCurveStrip curve={data.collectionsCurve} />}

                <p className="text-[10px] text-muted-foreground">
                    Click forecast cells to override. Clear a value to remove an override.
                </p>
                <p className="text-[10px] text-muted-foreground">
                    Source: Xero local mirror &middot; Generated{" "}
                    {data.generatedAt ? new Date(data.generatedAt).toLocaleString() : "now"}
                    {data.lastSyncedAt && (
                        <> &middot; Last sync: {formatRelativeTime(data.lastSyncedAt)}</>
                    )}
                </p>
            </CardContent>
            <CashFlowDetailedTour
                open={cashFlowTourOpen}
                activeTab={tourActiveTab}
                onClose={() => setCashFlowTourOpen(false)}
                onTabChange={handleTourTabChange}
            />
        </Card>
    );
}

// ── KPI Strip ──────────────────────────────────────────────

function KpiStrip({data}: {data: CashForecastData}) {
    const burnRate = data.burnRate;
    const minCashAlert = burnRate && burnRate.minCashAmount < 0;

    const isBurning = burnRate && burnRate.weeklyAvg > 0;

    return (
        <div className="flex gap-3 flex-wrap" data-testid="kpi-strip" data-tour="cashflow-kpis">
            <KpiCard
                label="Current Cash"
                value={formatCurrency(data.currentCash ?? 0, true)}
            />
            {isBurning && (
                <KpiCard
                    label="Runway"
                    value={burnRate?.runwayWeeks != null ? formatWeeks(burnRate.runwayWeeks) : "N/A"}
                    subtext={burnRate?.runwayWeeks != null && burnRate.runwayWeeks < 8 ? "Low runway" : undefined}
                    alert={burnRate?.runwayWeeks != null && burnRate.runwayWeeks < 8}
                />
            )}
            {isBurning && (
                <KpiCard
                    label="Avg Weekly Burn"
                    value={formatCurrency(burnRate?.weeklyAvg ?? 0, true)}
                />
            )}
            <KpiCard
                label="Min Cash Point"
                value={formatCurrency(burnRate?.minCashAmount ?? 0, true)}
                subtext={
                    burnRate?.minCashWeek != null
                        ? `Week ${burnRate.minCashWeek}`
                        : undefined
                }
                alert={!!minCashAlert}
            />
        </div>
    );
}

// ── Weekly matrix ──────────────────────────────────────────

// Unified column type — normalises actuals and forecast into one shape
interface UnifiedWeek {
    key: string;
    weekNumber: number;
    weekStarting: string;
    weekEnding?: string;
    confidence?: "high" | "medium" | "low";
    status: "actual" | "forecast";
    partial?: boolean;
    overdueAR?: number;
    overdueAP?: number;
    beginningCash: number;
    endingCash: number;
    totalInflow: number;
    totalOutflow: number;
    netCashFlow: number;
    categories: Record<string, ForecastCategoryAmounts>;
    derivations?: Record<string, import("@hooks/use-cashflow").CellDerivation>;
    xeroOpening?: number | null;
    xeroClosing?: number | null;
}

function toUnifiedWeeks(actuals: ActualWeek[], forecast: ForecastWeek[], currentCash: number): UnifiedWeek[] {
    const cols: UnifiedWeek[] = [];
    for (const a of actuals) {
        cols.push({
            key: `a${a.weekNumber}`,
            weekNumber: a.weekNumber,
            weekStarting: a.weekStarting,
            weekEnding: a.weekEnding,
            status: "actual",
            partial: a.partial,
            beginningCash: a.beginningCash,
            endingCash: a.endingCash,
            totalInflow: a.inflow,
            totalOutflow: a.outflow,
            netCashFlow: a.net,
            categories: a.categories ?? {},
            xeroOpening: a.xeroOpening,
            xeroClosing: a.xeroClosing,
        });
    }
    for (const f of forecast) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const fAny = f as any;
        cols.push({
            key: `f${f.weekNumber}`,
            weekNumber: f.weekNumber,
            weekStarting: f.weekStarting,
            weekEnding: f.weekEnding,
            confidence: f.confidence,
            status: "forecast",
            overdueAR: fAny.overdueAR,
            overdueAP: fAny.overdueAP,
            beginningCash: f.beginningCash ?? (f.weekNumber === 1 ? currentCash : f.cumulativeCash - f.netCashFlow),
            endingCash: f.endingCash ?? f.cumulativeCash,
            totalInflow: f.totalInflow,
            totalOutflow: f.totalOutflow,
            netCashFlow: f.netCashFlow,
            categories: f.categories,
            derivations: f.derivations,
            xeroOpening: null,
            xeroClosing: null,
        });
    }
    return cols;
}

function hasNonZeroUnified(cols: UnifiedWeek[], key: string, field: "inflow" | "outflow"): boolean {
    return cols.some((c) => {
        const cat = c.categories[key];
        return cat && Math.abs(field === "inflow" ? cat.inflow : cat.outflow) > 0.01;
    });
}

function getUnifiedCategoryValue(col: UnifiedWeek, key: string, type: "inflow" | "outflow"): number {
    const cat = col.categories[key];
    if (!cat) return 0;
    return type === "inflow" ? cat.inflow : -cat.outflow;
}

function getUnifiedGroupTotal(col: UnifiedWeek, keys: string[], type: "inflow" | "outflow"): number {
    return keys.reduce((sum, key) => sum + getUnifiedCategoryValue(col, key, type), 0);
}

function getXeroVariance(modelValue: number, xeroValue?: number | null): number | null {
    if (xeroValue == null) return null;
    return modelValue - xeroValue;
}

function formatXeroCheckValue(value?: number | null): string {
    return value == null ? "-" : formatCurrency(value, true);
}

function xeroVarianceClassName(variance: number | null): string {
    if (variance == null) return "text-amber-600";
    return Math.abs(variance) <= 1 ? "text-emerald-600" : "text-red-600 font-semibold";
}

function WeeklyMatrix({
    weeks,
    actuals,
    currentCash,
    weeklyOverrides,
    editingCell,
    editValue,
    editNote,
    savingCell,
    editInputRef,
    onEditValueChange,
    onEditNoteChange,
    onStartEdit,
    onCancelEdit,
    onCommitEdit,
}: {
    weeks: ForecastWeek[];
    actuals?: ActualWeek[];
    currentCash: number;
    weeklyOverrides?: Record<string, Record<string, WeeklyOverride>>;
    editingCell: EditingCell | null;
    editValue: string;
    editNote: string;
    savingCell: boolean;
    editInputRef: {current: HTMLInputElement | null};
    onEditValueChange: (value: string) => void;
    onEditNoteChange: (value: string) => void;
    onStartEdit: (cell: EditingCell) => void;
    onCancelEdit: () => void;
    onCommitEdit: () => Promise<void>;
}) {
    const cols = toUnifiedWeeks(actuals ?? [], weeks, currentCash);
    const actualCount = actuals?.length ?? 0;
    const [xeroCheckOpen, setXeroCheckOpen] = useState(false);
    const actualCols = cols.filter((c) => c.status === "actual");
    const hasMissingXero = actualCols.some((c) => c.xeroOpening == null || c.xeroClosing == null);
    const maxCashVariance = actualCols.reduce((max, c) => {
        const openingVariance = getXeroVariance(c.beginningCash, c.xeroOpening);
        const closingVariance = getXeroVariance(c.endingCash, c.xeroClosing);
        return Math.max(
            max,
            openingVariance == null ? 0 : Math.abs(openingVariance),
            closingVariance == null ? 0 : Math.abs(closingVariance),
        );
    }, 0);
    const xeroCheckStatus = hasMissingXero
        ? "Missing Xero snapshot"
        : maxCashVariance <= 1
          ? "Tied"
          : `Δ ${formatCurrency(maxCashVariance, true)}`;
    const xeroCheckRows: Array<{
        label: string;
        value: (col: UnifiedWeek) => number | null | undefined;
        isVariance?: boolean;
    }> = [
        {label: "Opening Cash (Model)", value: (c) => c.status === "actual" ? c.beginningCash : null},
        {label: "Opening Cash (Xero)", value: (c) => c.status === "actual" ? c.xeroOpening : null},
        {
            label: "Opening Cash (Var)",
            value: (c) => c.status === "actual" ? getXeroVariance(c.beginningCash, c.xeroOpening) : null,
            isVariance: true,
        },
        {label: "Closing Cash (Model)", value: (c) => c.status === "actual" ? c.endingCash : null},
        {label: "Closing Cash (Xero)", value: (c) => c.status === "actual" ? c.xeroClosing : null},
        {
            label: "Closing Cash (Var)",
            value: (c) => c.status === "actual" ? getXeroVariance(c.endingCash, c.xeroClosing) : null,
            isVariance: true,
        },
    ];

    return (
        <TooltipProvider>
            <div className="overflow-x-auto rounded-lg border border-gray-200" data-tour="cashflow-weekly-matrix">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-gray-50">
                            <TableHead className="sticky left-0 z-10 bg-gray-50 min-w-[160px]">
                                Category
                            </TableHead>
                            {cols.map((c, i) => {
                                const hasOverdue = (c.overdueAR ?? 0) > 0 || (c.overdueAP ?? 0) > 0;
                                return (
                                <TableHead
                                    key={c.key}
                                    className={`text-center min-w-[85px] ${
                                        c.status === "actual" ? "bg-gray-100" : ""
                                    } ${hasOverdue ? "bg-amber-50" : ""} ${i === actualCount - 1 ? "border-r-2 border-[#209DB3]" : ""}`}
                                >
                                    <div className="text-xs font-medium">
                                        {c.status === "actual" ? `A${c.weekNumber}` : `W${c.weekNumber}`}
                                        {c.partial && <span className="text-[9px] text-amber-600 ml-0.5">(partial)</span>}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground">
                                        {formatColumnDate(c)}
                                    </div>
                                    {c.status === "forecast" && c.confidence ? (
                                        <Badge
                                            variant="outline"
                                            className={`text-[9px] px-1 py-0 mt-0.5 ${CONFIDENCE_STYLES[c.confidence]}`}
                                        >
                                            {c.confidence}
                                        </Badge>
                                    ) : (
                                        <Badge variant="outline" className="text-[9px] px-1 py-0 mt-0.5 bg-gray-50 text-gray-500 border-gray-300">
                                            {c.partial ? "partial" : "actual"}
                                        </Badge>
                                    )}
                                    {hasOverdue && (
                                        <div className="text-[8px] text-amber-700 mt-0.5">
                                            {(c.overdueAR ?? 0) > 0 && <div>Overdue AR: {formatCurrency(c.overdueAR!, true)}</div>}
                                            {(c.overdueAP ?? 0) > 0 && <div>Overdue AP: {formatCurrency(c.overdueAP!, true)}</div>}
                                        </div>
                                    )}
                                </TableHead>
                                );
                            })}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {/* Opening Cash */}
                        <TableRow className="bg-[#209DB3]/5 font-semibold">
                            <TableCell className="sticky left-0 z-10 bg-[#edf7f9] text-xs font-semibold">
                                Opening Cash
                            </TableCell>
                            {cols.map((c, i) => (
                                <TableCell
                                    key={c.key}
                                    className={`text-center text-xs font-semibold tabular-nums ${c.beginningCash < 0 ? "text-red-600" : ""} ${
                                        c.status === "actual" ? "bg-[#209DB3]/5" : ""
                                    } ${i === actualCount - 1 ? "border-r-2 border-[#209DB3]" : ""}`}
                                >
                                    {formatCurrency(c.beginningCash, true)}
                                </TableCell>
                            ))}
                        </TableRow>

                        {/* Row groups */}
                        {ROW_GROUPS.map((group) => {
                            const visibleKeys = group.keys.filter((k) => hasNonZeroUnified(cols, k, group.type));
                            if (visibleKeys.length === 0) return null;

                            const groupTotals = cols.map((c) => getUnifiedGroupTotal(c, visibleKeys, group.type));

                            return (
                                <UnifiedGroupSection
                                    key={group.label}
                                    label={group.label}
                                    keys={visibleKeys}
                                    type={group.type}
                                    cols={cols}
                                    groupTotals={groupTotals}
                                    actualCount={actualCount}
                                    weeklyOverrides={weeklyOverrides}
                                    editingCell={editingCell}
                                    editValue={editValue}
                                    editNote={editNote}
                                    savingCell={savingCell}
                                    editInputRef={editInputRef}
                                    onEditValueChange={onEditValueChange}
                                    onEditNoteChange={onEditNoteChange}
                                    onStartEdit={onStartEdit}
                                    onCancelEdit={onCancelEdit}
                                    onCommitEdit={onCommitEdit}
                                />
                            );
                        })}

                        {/* Total Outflow */}
                        <TableRow className="border-t-2 border-gray-300 bg-gray-50">
                            <TableCell className="sticky left-0 z-10 bg-gray-50 font-semibold text-xs text-red-600">
                                Total Outflow
                            </TableCell>
                            {cols.map((c, i) => (
                                <TableCell
                                    key={c.key}
                                    className={`text-center text-xs font-medium text-red-500 tabular-nums ${
                                        c.status === "actual" ? "bg-gray-50" : ""
                                    } ${i === actualCount - 1 ? "border-r-2 border-[#209DB3]" : ""}`}
                                >
                                    {formatCurrency(-c.totalOutflow, true)}
                                </TableCell>
                            ))}
                        </TableRow>

                        {/* Net Cash Flow */}
                        <TableRow className="bg-gray-50">
                            <TableCell className="sticky left-0 z-10 bg-gray-50 font-semibold text-xs">
                                Net Cash Flow
                            </TableCell>
                            {cols.map((c, i) => (
                                <TableCell
                                    key={c.key}
                                    className={`text-center text-xs font-medium tabular-nums ${
                                        c.netCashFlow >= 0 ? "text-green-600" : "text-red-500"
                                    } ${i === actualCount - 1 ? "border-r-2 border-[#209DB3]" : ""}`}
                                >
                                    {formatCurrency(c.netCashFlow, true)}
                                </TableCell>
                            ))}
                        </TableRow>

                        {/* Closing Cash */}
                        <TableRow className="bg-[#209DB3]/5 border-t-2 border-[#209DB3]">
                            <TableCell className="sticky left-0 z-10 bg-[#edf7f9] font-bold text-xs text-[#209DB3]">
                                Closing Cash
                            </TableCell>
                            {cols.map((c, i) => (
                                <TableCell
                                    key={c.key}
                                    className={`text-center text-xs font-bold tabular-nums ${
                                        c.endingCash < 0
                                            ? "bg-red-100 text-red-700"
                                            : c.endingCash < currentCash * 0.25
                                              ? "bg-amber-50 text-amber-700"
                                              : "text-[#209DB3]"
                                    } ${i === actualCount - 1 ? "border-r-2 border-[#209DB3]" : ""}`}
                                >
                                    {formatCurrency(c.endingCash, true)}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableBody>
                    <TableFooter className="border-t-2 border-gray-300">
                        <TableRow className="bg-gray-100 hover:bg-gray-100">
                            <TableCell className="sticky left-0 z-10 bg-gray-100 p-0">
                                <button
                                    type="button"
                                    onClick={() => setXeroCheckOpen((open) => !open)}
                                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700"
                                >
                                    <span className="text-sm">{xeroCheckOpen ? "▾" : "▸"}</span>
                                    <span>Xero Bank Summary Check</span>
                                    <Badge
                                        variant="outline"
                                        className={`ml-auto text-[9px] ${
                                            hasMissingXero
                                                ? "border-amber-300 bg-amber-50 text-amber-700"
                                                : maxCashVariance <= 1
                                                  ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                                                  : "border-red-300 bg-red-50 text-red-700"
                                        }`}
                                    >
                                        {xeroCheckStatus}
                                    </Badge>
                                </button>
                            </TableCell>
                            {cols.map((c, i) => {
                                const openingVariance = getXeroVariance(c.beginningCash, c.xeroOpening);
                                const closingVariance = getXeroVariance(c.endingCash, c.xeroClosing);
                                const columnTied = c.status === "actual"
                                    && c.xeroOpening != null
                                    && c.xeroClosing != null
                                    && Math.abs(openingVariance ?? 0) <= 1
                                    && Math.abs(closingVariance ?? 0) <= 1;

                                return (
                                    <TableCell
                                        key={c.key}
                                        className={`text-center text-[10px] font-semibold tabular-nums ${
                                            c.status !== "actual"
                                                ? "text-gray-400"
                                                : columnTied
                                                  ? "text-emerald-600"
                                                  : "text-amber-600"
                                        } ${i === actualCount - 1 ? "border-r-2 border-[#209DB3]" : ""}`}
                                    >
                                        {c.status === "actual" ? (columnTied ? "Tied" : "Check") : "-"}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                        {xeroCheckOpen && xeroCheckRows.map((row) => (
                            <TableRow key={row.label} className="bg-gray-50 hover:bg-gray-50">
                                <TableCell className="sticky left-0 z-10 bg-gray-50 px-4 py-1.5 text-xs font-medium text-gray-600">
                                    {row.label}
                                </TableCell>
                                {cols.map((c, i) => {
                                    const value = row.value(c);
                                    const valueClass = row.isVariance
                                        ? xeroVarianceClassName(value ?? null)
                                        : value == null && c.status === "actual"
                                          ? "text-amber-600"
                                          : "text-gray-600";

                                    return (
                                        <TableCell
                                            key={`${row.label}-${c.key}`}
                                            className={`text-center text-xs tabular-nums ${valueClass} ${
                                                i === actualCount - 1 ? "border-r-2 border-[#209DB3]" : ""
                                            }`}
                                        >
                                            {formatXeroCheckValue(value)}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableFooter>
                </Table>
            </div>
        </TooltipProvider>
    );
}

// ── Group section ──────────────────────────────────────────

function UnifiedGroupSection({
    label,
    keys,
    type,
    cols,
    groupTotals,
    actualCount,
    weeklyOverrides,
    editingCell,
    editValue,
    editNote,
    savingCell,
    editInputRef,
    onEditValueChange,
    onEditNoteChange,
    onStartEdit,
    onCancelEdit,
    onCommitEdit,
}: {
    label: string;
    keys: string[];
    type: "inflow" | "outflow";
    cols: UnifiedWeek[];
    groupTotals: number[];
    actualCount: number;
    weeklyOverrides?: Record<string, Record<string, WeeklyOverride>>;
    editingCell: EditingCell | null;
    editValue: string;
    editNote: string;
    savingCell: boolean;
    editInputRef: {current: HTMLInputElement | null};
    onEditValueChange: (value: string) => void;
    onEditNoteChange: (value: string) => void;
    onStartEdit: (cell: EditingCell) => void;
    onCancelEdit: () => void;
    onCommitEdit: () => Promise<void>;
}) {
    return (
        <>
            {/* Group header */}
            <TableRow className="bg-muted/40">
                <TableCell className="sticky left-0 z-20 min-w-[160px] bg-muted font-semibold text-[11px] uppercase tracking-wider text-muted-foreground">
                    {label}
                </TableCell>
                {cols.map((c, i) => (
                    <TableCell
                        key={`group-${label}-${c.key}`}
                        className={`bg-muted/40 ${i === actualCount - 1 ? "border-r-2 border-[#209DB3]" : ""}`}
                    />
                ))}
            </TableRow>

            {/* Category rows */}
            {keys.map((key) => (
                <TableRow key={key} className="hover:bg-muted/20">
                    <TableCell className="sticky left-0 z-10 bg-white pl-6 text-xs text-[#1e293b]">
                        {key === "unclassifiedCashReceipts" || key === "unclassifiedCashPayments" ? (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="underline decoration-dotted cursor-help text-amber-700">
                                        {CATEGORY_LABELS[key]}
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent side="right" className="text-xs max-w-[260px]">
                                    Cash movements from bank summary not yet classified by transaction type. Detailed classification requires Xero Payments API — coming next release.
                                </TooltipContent>
                            </Tooltip>
                        ) : (
                            CATEGORY_LABELS[key] || key
                        )}
                    </TableCell>
                    {cols.map((c, i) => {
                        const val = getUnifiedCategoryValue(c, key, type);
                        const isZero = Math.abs(val) < 0.01;
                        const ovr = c.status === "forecast" ? weeklyOverrides?.[String(c.weekNumber)]?.[key] : undefined;
                        const isOverridden = !!ovr;
                        const isActual = c.status === "actual";
                        const cellId = `${c.weekNumber}:${key}:${type}`;
                        const isEditing = editingCell?.cellId === cellId;
                        const isEditable = c.status === "forecast";
                        if (isEditing) {
                            return (
                                <TableCell
                                    key={c.key}
                                    className={`text-center p-1 bg-blue-50 relative ${i === actualCount - 1 ? "border-r-2 border-[#209DB3]" : ""}`}
                                >
                                    <span className="text-xs tabular-nums font-semibold text-blue-700">
                                        {editValue || formatCurrency(val, true)}
                                    </span>
                                    <div
                                        className="absolute z-50 top-full left-1/2 -translate-x-1/2 mt-1 w-56 rounded-lg border bg-white shadow-lg p-3 space-y-2"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className="flex items-center justify-between">
                                            <p className="text-xs font-semibold text-[#209DB3]">
                                                {CATEGORY_LABELS[key] || key} — W{c.weekNumber}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-medium text-muted-foreground uppercase">Value ($)</label>
                                            <Input
                                                ref={editInputRef}
                                                data-testid={`week13-input-${cellId}`}
                                                value={editValue}
                                                onChange={(e) => onEditValueChange(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        void onCommitEdit();
                                                    } else if (e.key === "Escape") {
                                                        e.preventDefault();
                                                        onCancelEdit();
                                                    }
                                                }}
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                disabled={savingCell}
                                                className="h-7 text-xs tabular-nums mt-0.5"
                                                placeholder={String(Math.abs(val))}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-medium text-muted-foreground uppercase">Note (optional)</label>
                                            <Input
                                                value={editNote}
                                                onChange={(e) => onEditNoteChange(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        void onCommitEdit();
                                                    } else if (e.key === "Escape") {
                                                        e.preventDefault();
                                                        onCancelEdit();
                                                    }
                                                }}
                                                disabled={savingCell}
                                                className="h-7 text-xs mt-0.5"
                                                placeholder="e.g. Client confirmed payment"
                                            />
                                        </div>
                                        {ovr && (
                                            <p className="text-[10px] text-muted-foreground">
                                                Original: {formatCurrency(ovr.original)}
                                            </p>
                                        )}
                                        <div className="flex gap-1.5 pt-1">
                                            <Button
                                                size="sm"
                                                className="h-6 text-xs flex-1 bg-[#209DB3] hover:bg-[#1a7d8f]"
                                                disabled={savingCell}
                                                onClick={() => void onCommitEdit()}
                                            >
                                                {savingCell ? "Saving…" : "Save"}
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-6 text-xs flex-1"
                                                disabled={savingCell}
                                                onClick={onCancelEdit}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>
                                </TableCell>
                            );
                        }
                        return (
                            <Tooltip key={c.key}>
                                <TooltipTrigger asChild>
                                    <TableCell
                                        data-testid={`week13-cell-${cellId}`}
                                        className={`text-center p-1 ${isOverridden ? "bg-blue-50" : isActual ? "bg-gray-50/50" : ""} ${isEditable ? "cursor-pointer" : ""} ${i === actualCount - 1 ? "border-r-2 border-[#209DB3]" : ""}`}
                                        onClick={() => {
                                            if (!isEditable) return;
                                            onStartEdit({
                                                cellId,
                                                weekNumber: c.weekNumber,
                                                categoryKey: key,
                                                field: type,
                                                currentValue: Math.abs(val),
                                                overrideOpId: ovr?.op_id,
                                            });
                                        }}
                                    >
                                        <span
                                            className={`text-xs tabular-nums cursor-default ${
                                                isOverridden
                                                    ? "font-semibold text-blue-700"
                                                    : isZero
                                                      ? "text-[#94a3b8]"
                                                      : val > 0
                                                        ? "text-green-600"
                                                        : "text-red-500"
                                            }`}
                                        >
                                            {isZero ? "-" : formatCurrency(val, true)}
                                        </span>
                                    </TableCell>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[280px]">
                                    <div className="text-xs space-y-0.5">
                                        <p className="font-medium">{CATEGORY_LABELS[key] || key}</p>
                                        {isActual && (
                                            <>
                                                <p>In: {formatCurrency(c.categories[key]?.inflow ?? 0)}</p>
                                                <p>Out: {formatCurrency(c.categories[key]?.outflow ?? 0)}</p>
                                                <p className="text-gray-500 font-medium mt-1">Source: Xero actuals</p>
                                            </>
                                        )}
                                        {!isActual && (() => {
                                            const drv = c.derivations?.[key];
                                            if (!drv) return (
                                                <>
                                                    <p>In: {formatCurrency(c.categories[key]?.inflow ?? 0)}</p>
                                                    <p>Out: {formatCurrency(c.categories[key]?.outflow ?? 0)}</p>
                                                </>
                                            );
                                            const lines: React.ReactNode[] = [];
                                            if (type === "inflow") {
                                                lines.push(<p key="avg">13-wk avg: {formatCurrency(drv.avgInflow)}</p>);
                                                if (drv.scheduledAR != null)
                                                    lines.push(<p key="ar">AR scheduled (invoices due): {formatCurrency(drv.scheduledAR)}</p>);
                                                if (drv.repeatingInflow)
                                                    lines.push(<p key="rep">+ Repeating invoices: {formatCurrency(drv.repeatingInflow)}</p>);
                                                if (drv.basis === "arScheduled")
                                                    lines.push(<p key="rule" className="text-blue-600 mt-0.5">Using AR scheduled (higher than avg)</p>);
                                                else if (drv.basis === "weeklyAvg")
                                                    lines.push(<p key="rule" className="text-gray-500 mt-0.5">Using weekly average baseline</p>);
                                            } else {
                                                lines.push(<p key="avg">13-wk avg: {formatCurrency(drv.avgOutflow)}</p>);
                                                if (drv.scheduledAP != null)
                                                    lines.push(<p key="ap">AP scheduled (bills due): {formatCurrency(drv.scheduledAP)}</p>);
                                                if (drv.repeatingOutflow)
                                                    lines.push(<p key="rep">+ Repeating invoices: {formatCurrency(drv.repeatingOutflow)}</p>);
                                                if (drv.basis === "apScheduled")
                                                    lines.push(<p key="rule" className="text-blue-600 mt-0.5">Using AP scheduled (higher than avg)</p>);
                                                else if (drv.basis === "basCycle")
                                                    lines.push(<p key="rule" className="text-blue-600 mt-0.5">BAS payment due this week: {formatCurrency(drv.basAmount ?? 0)}</p>);
                                                else if (drv.basis === "basCycleNoDue")
                                                    lines.push(<p key="rule" className="text-gray-500 mt-0.5">No BAS payment due this week</p>);
                                                else if (drv.basis === "weeklyAvg")
                                                    lines.push(<p key="rule" className="text-gray-500 mt-0.5">Using weekly average baseline</p>);
                                            }
                                            lines.push(<p key="result" className="font-medium mt-0.5 border-t border-white/20 pt-0.5">= {formatCurrency(Math.abs(val))}</p>);
                                            return lines;
                                        })()}
                                        {isOverridden && ovr && (
                                            <>
                                                <p className="text-blue-600 font-medium mt-1">Override active</p>
                                                <p className="text-muted-foreground">
                                                    Original {ovr.field}: {formatCurrency(ovr.original)}
                                                </p>
                                                {ovr.label && ovr.label.includes(" — ") && (
                                                    <p className="text-muted-foreground italic">
                                                        {ovr.label.split(" — ").slice(1).join(" — ")}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                        {isEditable && !isOverridden && <p className="text-muted-foreground mt-1">Click to edit</p>}
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        );
                    })}
                </TableRow>
            ))}

            {/* Group total */}
            <TableRow className="border-t">
                <TableCell className={`sticky left-0 z-10 bg-white pl-4 text-xs font-semibold ${type === "inflow" ? "text-green-700" : "text-red-600"}`}>
                    Total {label}
                </TableCell>
                {cols.map((c, i) => (
                    <TableCell
                        key={c.key}
                        className={`text-center text-xs font-medium tabular-nums ${
                            type === "inflow" ? "text-green-600" : "text-red-500"
                        } ${i === actualCount - 1 ? "border-r-2 border-[#209DB3]" : ""}`}
                    >
                        {formatCurrency(groupTotals[i], true)}
                    </TableCell>
                ))}
            </TableRow>
        </>
    );
}

// ── Scenario strip ─────────────────────────────────────────

function ScenarioStrip({scenarios}: {scenarios: NonNullable<CashForecastData["scenarios"]>}) {
    return (
        <div className="flex gap-3 flex-wrap" data-testid="scenario-strip" data-tour="cashflow-scenarios">
            <ScenarioCard
                label="Pessimistic"
                endCash={scenarios.pessimistic.endCash}
                runway={scenarios.pessimistic.runwayWeeks}
                color="text-red-600"
                borderColor="border-red-200"
            />
            <ScenarioCard
                label="Base Case"
                endCash={scenarios.base.endCash}
                runway={scenarios.base.runwayWeeks}
                color="text-[#209DB3]"
                borderColor="border-[#209DB3]/30"
            />
            <ScenarioCard
                label="Optimistic"
                endCash={scenarios.optimistic.endCash}
                runway={scenarios.optimistic.runwayWeeks}
                color="text-green-600"
                borderColor="border-green-200"
            />
        </div>
    );
}

function ScenarioCard({label, endCash, runway, color, borderColor}: {
    label: string;
    endCash: number;
    runway: number | null;
    color: string;
    borderColor: string;
}) {
    return (
        <Card className={`flex-1 min-w-[140px] ${borderColor}`}>
            <CardContent className="p-3">
                <p className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</p>
                <p className={`text-base font-bold tabular-nums mt-0.5 ${color}`}>
                    {formatCurrency(endCash, true)}
                </p>
                <p className="text-[10px] text-muted-foreground">
                    Runway: {runway != null ? formatWeeks(runway) : "N/A"}
                </p>
            </CardContent>
        </Card>
    );
}

// ── Tie-out status strip ──────────────────────────────────

function TieOutStrip({tieOut}: {tieOut: TieOutData}) {
    const checks = [
        {label: "Formula Tie", status: tieOut.formulaTie.status, tip: "Opening + Inflows \u2212 Outflows = Closing each week"},
        {label: "Chain Tie", status: tieOut.chainTie.status, tip: "Each week\u2019s closing cash equals the next week\u2019s opening cash"},
        {label: "Bank Anchor", status: tieOut.bankAnchor.status, tip: "Opening cash matches the Xero bank statement balance"},
    ];

    return (
        <TooltipProvider>
            <div className="flex gap-2 flex-wrap" data-testid="tieout-strip">
                {checks.map((c) => {
                    const isPass = c.status === "pass";
                    const Icon = isPass ? CheckCircleIcon : XCircleIcon;
                    return (
                        <Tooltip key={c.label}>
                            <TooltipTrigger asChild>
                                <Badge
                                    variant="outline"
                                    className={`text-[11px] px-2 py-1 gap-1 cursor-default ${
                                        isPass
                                            ? "bg-green-50 text-green-700 border-green-200"
                                            : "bg-red-50 text-red-700 border-red-200"
                                    }`}
                                >
                                    <Icon className="size-3" />
                                    {c.label}
                                </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="text-xs max-w-[220px]">
                                {c.tip}
                            </TooltipContent>
                        </Tooltip>
                    );
                })}
            </div>
        </TooltipProvider>
    );
}

// ── Variance table ────────────────────────────────────────

function VarianceTable({actuals}: {actuals: ActualWeek[]}) {
    const withVariance = actuals.filter((a) => a.variance != null);

    if (withVariance.length === 0) {
        return (
            <div className="rounded-lg border border-gray-200 p-4 text-center text-xs text-muted-foreground">
                No variance data yet. Snapshots will be captured on each forecast load
                and matched to completed weeks automatically.
            </div>
        );
    }

    return (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
            <Table>
                <TableHeader>
                    <TableRow className="bg-gray-50">
                        <TableHead className="text-xs">Week</TableHead>
                        <TableHead className="text-xs text-right">Forecast Net</TableHead>
                        <TableHead className="text-xs text-right">Actual Net</TableHead>
                        <TableHead className="text-xs text-right">Delta ($)</TableHead>
                        <TableHead className="text-xs text-right">Variance (%)</TableHead>
                        <TableHead className="text-xs">Notes</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {withVariance.map((a) => {
                        const v = a.variance!;
                        const favorable = v.netDelta >= 0;
                        return (
                            <TableRow key={a.weekNumber}>
                                <TableCell className="text-xs">
                                    <div>W{a.weekNumber}</div>
                                    <div className="text-[10px] text-muted-foreground">
                                        {formatColumnDate(a)}
                                    </div>
                                </TableCell>
                                <TableCell className="text-xs text-right tabular-nums">
                                    {a.forecastSnapshot
                                        ? formatCurrency(a.forecastSnapshot.netCashFlow, true)
                                        : "--"}
                                </TableCell>
                                <TableCell className="text-xs text-right tabular-nums">
                                    {formatCurrency(a.net, true)}
                                </TableCell>
                                <TableCell
                                    className={`text-xs text-right tabular-nums font-medium ${
                                        favorable ? "text-green-600" : "text-red-500"
                                    }`}
                                >
                                    {formatCurrency(v.netDelta, true)}
                                </TableCell>
                                <TableCell
                                    className={`text-xs text-right tabular-nums ${
                                        favorable ? "text-green-600" : "text-red-500"
                                    }`}
                                >
                                    {v.netPct != null ? `${v.netPct > 0 ? "+" : ""}${v.netPct}%` : "--"}
                                </TableCell>
                                <TableCell className="text-xs text-muted-foreground">
                                    --
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
}

// ── Alert banners ──────────────────────────────────────────

function AlertBanners({alerts}: {alerts: NonNullable<CashForecastData["alerts"]>}) {
    return (
        <div className="space-y-2" data-testid="alert-banners">
            {alerts.map((alert, i) => {
                const Icon = ALERT_ICON[alert.severity];
                return (
                    <div key={i} className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${ALERT_STYLE[alert.severity]}`}>
                        <Icon className="size-4 shrink-0 mt-0.5" />
                        <span>{alert.message}</span>
                    </div>
                );
            })}
        </div>
    );
}

// ── AR/AP control reconciliation note ─────────────────────

function ControlReconciliationNote({data}: {data: CashForecastData}) {
    const ar = data.controlReconciliation?.ar;
    const ap = data.controlReconciliation?.ap;
    const active = [ar, ap].filter((item) => item && item.action !== "none");
    if (active.length === 0) return null;

    const period = data.balanceSheetControlPeriod
        ? formatWeekHeader(data.balanceSheetControlPeriod)
        : "latest Balance Sheet";

    return (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <span className="font-semibold">AR/AP control tie-out:</span>{" "}
            Opening AR/AP are tied to the Xero Balance Sheet control accounts as at {period}.
            When invoice-level detail is stale or materially different, Pecunio uses an explicit
            unallocated control bucket instead of showing misleading debtor/payable detail.
        </div>
    );
}

// ── AR Roll-Forward Table ─────────────────────────────────

function ARRollforwardTable({rows}: {rows: ARRollforwardWeek[]}) {
    return (
        <div className="space-y-1">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">AR Roll-Forward</p>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-gray-50">
                            <TableHead className="sticky left-0 z-10 bg-gray-50 min-w-[120px] text-xs">Metric</TableHead>
                            {rows.map((r) => (
                                <TableHead key={r.weekNumber} className="text-center min-w-[80px] text-xs">
                                    W{r.weekNumber}
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        <TableRow>
                            <TableCell className="sticky left-0 z-10 bg-white text-xs font-medium">Opening AR</TableCell>
                            {rows.map((r) => (
                                <TableCell key={r.weekNumber} className="text-center text-xs tabular-nums">
                                    {formatCurrency(r.openingAR, true)}
                                </TableCell>
                            ))}
                        </TableRow>
                        <TableRow>
                            <TableCell className="sticky left-0 z-10 bg-white text-xs font-medium text-blue-600">+ Invoiced</TableCell>
                            {rows.map((r) => (
                                <TableCell key={r.weekNumber} className="text-center text-xs tabular-nums text-blue-600">
                                    {r.invoiced > 0 ? formatCurrency(r.invoiced, true) : "-"}
                                </TableCell>
                            ))}
                        </TableRow>
                        <TableRow>
                            <TableCell className="sticky left-0 z-10 bg-white text-xs font-medium text-green-600">- Collected</TableCell>
                            {rows.map((r) => (
                                <TableCell key={r.weekNumber} className="text-center text-xs tabular-nums text-green-600">
                                    {r.collected > 0 ? formatCurrency(-r.collected, true) : "-"}
                                </TableCell>
                            ))}
                        </TableRow>
                        <TableRow className="bg-gray-50 border-t">
                            <TableCell className="sticky left-0 z-10 bg-gray-50 text-xs font-semibold">Closing AR</TableCell>
                            {rows.map((r) => (
                                <TableCell key={r.weekNumber} className="text-center text-xs font-semibold tabular-nums">
                                    {formatCurrency(r.closingAR, true)}
                                </TableCell>
                            ))}
                        </TableRow>
                        <TableRow>
                            <TableCell className="sticky left-0 z-10 bg-white text-xs font-medium text-muted-foreground">Implied DSO</TableCell>
                            {rows.map((r) => (
                                <TableCell key={r.weekNumber} className="text-center text-xs tabular-nums text-muted-foreground">
                                    {r.impliedDSO != null ? `${r.impliedDSO}d` : "-"}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

function APRollforwardTable({rows}: {rows: APRollforwardWeek[]}) {
    return (
        <div className="space-y-1">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">AP Roll-Forward</p>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-gray-50">
                            <TableHead className="sticky left-0 z-10 bg-gray-50 min-w-[120px] text-xs">Metric</TableHead>
                            {rows.map((r) => (
                                <TableHead key={r.weekNumber} className="text-center min-w-[80px] text-xs">
                                    W{r.weekNumber}
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        <TableRow>
                            <TableCell className="sticky left-0 z-10 bg-white text-xs font-medium">Opening AP</TableCell>
                            {rows.map((r) => (
                                <TableCell key={r.weekNumber} className="text-center text-xs tabular-nums">
                                    {formatCurrency(r.openingAP, true)}
                                </TableCell>
                            ))}
                        </TableRow>
                        <TableRow>
                            <TableCell className="sticky left-0 z-10 bg-white text-xs font-medium text-blue-600">+ New Bills</TableCell>
                            {rows.map((r) => (
                                <TableCell key={r.weekNumber} className="text-center text-xs tabular-nums text-blue-600">
                                    {r.billed > 0 ? formatCurrency(r.billed, true) : "-"}
                                </TableCell>
                            ))}
                        </TableRow>
                        <TableRow>
                            <TableCell className="sticky left-0 z-10 bg-white text-xs font-medium text-red-600">- Paid</TableCell>
                            {rows.map((r) => (
                                <TableCell key={r.weekNumber} className="text-center text-xs tabular-nums text-red-600">
                                    {r.paid > 0 ? formatCurrency(-r.paid, true) : "-"}
                                </TableCell>
                            ))}
                        </TableRow>
                        <TableRow className="bg-gray-50 border-t">
                            <TableCell className="sticky left-0 z-10 bg-gray-50 text-xs font-semibold">Closing AP</TableCell>
                            {rows.map((r) => (
                                <TableCell key={r.weekNumber} className="text-center text-xs font-semibold tabular-nums">
                                    {formatCurrency(r.closingAP, true)}
                                </TableCell>
                            ))}
                        </TableRow>
                        <TableRow>
                            <TableCell className="sticky left-0 z-10 bg-white text-xs font-medium text-muted-foreground">Implied DPO</TableCell>
                            {rows.map((r) => (
                                <TableCell key={r.weekNumber} className="text-center text-xs tabular-nums text-muted-foreground">
                                    {r.impliedDPO != null ? `${r.impliedDPO}d` : "-"}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

// ── Concentration Risk Panel ──────────────────────────────

function ConcentrationRiskPanel({data}: {data: ConcentrationRisk}) {
    return (
        <div className="space-y-1">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                AR Concentration — Top Customers
            </p>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-gray-50">
                            <TableHead className="text-xs">Customer</TableHead>
                            <TableHead className="text-right text-xs">Outstanding</TableHead>
                            <TableHead className="text-right text-xs">Share</TableHead>
                            <TableHead className="text-right text-xs">Avg Days to Pay</TableHead>
                            <TableHead className="text-right text-xs">Paid Invoices</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {data.topCustomers.map((c) => (
                            <TableRow key={c.contactId}>
                                <TableCell className="text-xs font-medium">
                                    {c.name}
                                    {c.isControlBucket && (
                                        <Badge variant="outline" className="ml-2 text-[9px] bg-amber-50 text-amber-700 border-amber-200">
                                            control
                                        </Badge>
                                    )}
                                </TableCell>
                                <TableCell className="text-right text-xs tabular-nums">{formatCurrency(c.outstandingAR, true)}</TableCell>
                                <TableCell className="text-right text-xs tabular-nums">
                                    <span className={c.shareOfTotalAR > 30 && !c.isControlBucket ? "text-amber-600 font-semibold" : ""}>
                                        {c.shareOfTotalAR.toFixed(0)}%
                                    </span>
                                </TableCell>
                                <TableCell className="text-right text-xs tabular-nums">
                                    {c.avgDaysToPay != null ? `${c.avgDaysToPay}d` : "-"}
                                </TableCell>
                                <TableCell className="text-right text-xs tabular-nums">{c.invoiceCount}</TableCell>
                            </TableRow>
                        ))}
                        <TableRow className="bg-gray-50 border-t">
                            <TableCell className="text-xs font-semibold">Total AR</TableCell>
                            <TableCell className="text-right text-xs font-semibold tabular-nums">{formatCurrency(data.totalAR, true)}</TableCell>
                            <TableCell className="text-right text-xs font-semibold">100%</TableCell>
                            <TableCell />
                            <TableCell />
                        </TableRow>
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

// ── Collections Curve Strip ───────────────────────────────

function CollectionsCurveStrip({curve}: {curve: number[]}) {
    const labels = ["Due week", "+1 wk", "+2 wk", "+3 wk", "+4 wk", "5+ wk"];
    return (
        <div className="space-y-1">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Collections Curve (% of AR collected by week after due date)
            </p>
            <div className="flex gap-2 flex-wrap">
                {curve.map((pct, i) => (
                    <div key={i} className="flex-1 min-w-[70px] rounded border px-2 py-1.5 text-center">
                        <p className="text-[10px] text-muted-foreground">{labels[i] ?? `+${i} wk`}</p>
                        <p className="text-sm font-bold tabular-nums text-[#209DB3]">{(pct * 100).toFixed(0)}%</p>
                    </div>
                ))}
            </div>
        </div>
    );
}
